import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { generateServiceSchema } from "@/data/serviceSchemaData";
import { Header } from "@/components/Header";
import { PackageCallout } from "@/components/PackageCallout";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Footer } from "@/components/Footer";
import { YouTubeShortsSection } from "@/components/YouTubeShortsSection";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CTASection } from "@/components/CTASection";
import { RelatedServices } from "@/components/RelatedServices";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";

// Images
import heroImage from "@/assets/fire-pit-installation-north-caldwell-nj-2.webp";
import kinnelonFirePit from "@/assets/fire-pit-installation-kinnelon-nj-2.webp";
import cliftonPatioFirePit from "@/assets/masonry-paver-patio-clifton-nj-2.webp";
import mountainLakesPatioFirePit from "@/assets/patio-pavers-mountain-lakes-morris-county-nj-3.webp";

// Citation link component - wraps text with citation number
const CitationLink = ({ num, url, children }: { num: number; url: string; children?: React.ReactNode }) => (
  <a 
    href={url} 
    target="_blank" 
    rel="noopener noreferrer" 
    className="text-secondary hover:underline"
  >
    {children}<sup>{num}</sup>
  </a>
);

// Placeholder image component for future images
const ImagePlaceholder = ({ alt, className = "" }: { alt: string; className?: string }) => (
  <div className={`bg-muted rounded-xl flex items-center justify-center ${className}`}>
    <div className="text-center p-8">
      <Skeleton className="w-full h-full absolute inset-0 rounded-xl" />
      <span className="relative text-muted-foreground text-sm">{alt}</span>
    </div>
  </div>
);

const faqs = [
  {
    question: "Do I need a permit to install a fire pit in North Jersey?",
    answer: "Yes, permanent masonry and gas fire pits require a permit under New Jersey's Uniform Construction Code. Contact your local building department before starting any work. Requirements can vary between municipalities, so it is worth confirming the rules for your specific town.",
  },
  {
    question: "How far does a fire pit need to be from my house in New Jersey?",
    answer: "Approved fire pit containers must be placed at least 15 feet from any structure or property line. Non-approved setups require a minimum of 25 feet. Some North Jersey municipalities enforce stricter setback rules, so check with your local building department to be sure.",
  },
  {
    question: "What type of fire pit works best in North Jersey's climate?",
    answer: "A masonry fire pit built on a compacted gravel base handles the region's freeze-thaw cycles well. The base allows water to drain instead of pooling and freezing beneath the structure. Gas fire pits are a popular choice for homeowners with smaller lots where smoke and setback distances are a concern.",
  },
  {
    question: "Can a masonry contractor install a gas fire pit?",
    answer: "A masonry contractor builds the fire pit structure itself. Gas line connections require a licensed plumber or gas professional. Many North Jersey masonry contractors coordinate both trades on a single project so you do not have to manage separate schedules.",
  },
  {
    question: "When is the best time to install a fire pit in North Jersey?",
    answer: "Late spring through early fall is the best window. The ground is workable, mortar cures properly in warmer temperatures, and you can enjoy the fire pit the same season it is installed. Scheduling earlier in the season helps avoid the summer backlog most contractors deal with.",
  },
  {
    question: "How do I maintain my masonry fire pit?",
    answer: "Let ashes cool completely before removing them. In winter, use a weather-resistant cover to protect from snow and ice. Each spring, inspect mortar joints for cracks and repoint early if needed. For gas fire pits, check connections and burners before your first use of the season.",
  },
];

const citations = [
  {
    num: 1,
    text: "National Fire Protection Association (NFPA) - Wildfire Prevention Tips for Fire Pits and Outdoor Fires – Fire pit safety guidelines, setback recommendations, injury data.",
    url: "https://www.nfpa.org/news-blogs-and-articles/blogs/2025/09/10/wildfire-prevention-tips-for-fire-pits-and-outdoor-fires",
  },
  {
    num: 2,
    text: "U.S. Fire Administration (FEMA) - Outdoor Fire Safety – Federal outdoor fire safety guidelines, fire pit placement (10+ feet from structures), supervision requirements.",
    url: "https://www.usfa.fema.gov/prevention/outdoor-fires/",
  },
  {
    num: 3,
    text: "U.S. Environmental Protection Agency (EPA) - Backyard Recreational Fires (Burnwise Program) – Air quality considerations, wood smoke health effects, gas conversion benefits.",
    url: "https://www.epa.gov/burnwise/backyard-recreational-fires",
  },
  {
    num: 4,
    text: "New Jersey Department of Environmental Protection (NJ DEP) - Forest Fire Service Recreational Fire Permits – NJ-specific permit requirements, burning restrictions, seasonal fire bans.",
    url: "https://www.nj.gov/dep/parksandforests/fire/permits/recreational.html",
  },
  {
    num: 5,
    text: "U.S. Consumer Product Safety Commission (CPSC) - Fire Safety Vigilance / Burn Awareness – Outdoor fire pit injury statistics, product safety standards, burn prevention data.",
    url: "https://www.cpsc.gov/Newsroom/News-Releases/2023/CPSC-Urges-Fire-Safety-Vigilance-During-Burn-Awareness-Week",
  },
];

const youtubeShorts = [
  {
    url: "https://www.youtube.com/embed/KjTHv1gQP9c",
    title: "Fire Pit Installation Video 1",
  },
  {
    url: "https://www.youtube.com/embed/plh-ZGZz65A",
    title: "Fire Pit Installation Video 2",
  },
  {
    url: "https://www.youtube.com/embed/Fogsfyz7P18",
    title: "Fire Pit Installation Video 3",
  },
];

export default function FireplacesFirepits() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Fire Pit Installation Totowa, NJ | Masonry Pro Construction</title>
        <meta name="description" content="Custom fire pits built to last through North Jersey winters. We handle permits, footings, and gas line coordination. Stone, brick, or block, your choice." />
        <meta property="og:description" content="Custom fire pits built to last through North Jersey winters. We handle permits, footings, and gas line coordination. Stone, brick, or block, your choice." />
        <meta property="og:title" content="Fire Pit Installation Totowa, NJ | Masonry Pro Construction" />
        <script type="application/ld+json">
          {JSON.stringify(generateServiceSchema({
            name: "Fire Pit Installation",
            description: "Custom fire pit installation built for North Jersey weather. Permits, base prep, and final inspection included.",
            url: "/masonry-services/fire-pits",
            image: "https://masonry-pro.com/og/fire-pits.jpg"
          }))}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/fire-pits.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/fire-pits.jpg" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />
        
        <main>
          {/* Hero Section */}
          <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '100px' }}>
            <img src={heroImage} alt="Outdoor fireplace and fire pit construction in North Jersey" width={1200} height={800} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(13,16,23,0.50), rgba(13,16,23,0.70))' }} />
            <div className="container mx-auto px-4 w-full relative z-10">
              <div className="max-w-4xl mx-auto text-center" style={{ padding: '2rem' }}>
                <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1.5rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                  Fire Pit Installation in Totowa, NJ Serving North Jersey:{' '}
                  <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>Built to Last Every Season</span>
                </h1>
                <Button variant="gold" size="lg" asChild>
                  <a href="tel:+19737252395" style={{ color: '#0d1017' }}>Schedule Your Free Assessment</a>
                </Button>
              </div>
            </div>

            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <button
                onClick={() => document.getElementById("intro")?.scrollIntoView({ behavior: "smooth" })}
                className="text-secondary hover:text-secondary/80 transition-colors animate-bounce"
                aria-label="Scroll down"
              >
                <ChevronDown className="h-10 w-10" />
              </button>
            </div>
          </section>

          <PackageCallout packageId="backyard-retreat" packageName="Backyard Retreat Package" blurb={"Oversized paver patio with a structural pergola pad and an outdoor fireplace as the focal point, built as one entertaining footprint for year-round use."} />

          {/* Intro Section */}
          <section id="intro" className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto prose prose-lg">
                <p className="text-lg text-muted-foreground">
                  A custom fire pit lets you use your backyard well past summer. Whether it's fall nights with family or winter get-togethers, a fire pit adds real value to your outdoor space. This page covers fire pit installation in North Jersey, from material options to base prep, permits, and what to expect when our masonry crew handles the build. Fire pits are one piece of our <Link to="/" className="text-secondary hover:underline">full range of masonry work</Link>.
                </p>
              </div>
            </div>
          </section>

          {/* Materials Section - Content LEFT, Placeholder RIGHT */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    The Right Fire Pit Materials <span className="text-gradient-gold">Protect Your Investment</span> for Years
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      Natural stone, brick, and concrete block are the go-to materials for masonry fire pits in North Jersey. Each of them can withstand repeated exposure to high heat without cracking or degrading. That's especially important when you're burning a fire every weekend from September through March.
                    </p>
                    <p>
                      A stainless steel liner sits inside the walls and takes the direct heat, so the masonry doesn't have to. Without one, even solid stone can wear down faster than it should.
                    </p>
                    <p>
                      So that you are aware, standard concrete blocks are not rated for fire pit use. They can crack or pop apart under high temperatures. We see it on older DIY builds in Ridgewood and Montclair frequently. Fire-rated materials cost a bit more upfront, but they last for years without needing replacement.
                    </p>
                    <p>
                      A full custom fire pit or outdoor fireplace with a seating wall and stone veneer is a meaningful project, and many of our customers use financing to make the timeline work without stretching their budget. We partner with Acorn Finance, which lets you compare pre-qualified offers from multiple lenders in about a minute. The soft credit check will not affect your credit score. <Link to="/financing" className="text-secondary hover:underline">Explore financing options</Link>.
                    </p>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden relative">
                  <img
                    src={cliftonPatioFirePit}
                    alt="Masonry paver patio with a fire pit and retaining wall, installed by Masonry Pro Construction at Clifton, Passaic County, New Jersey, 07013"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width="800"
                    height="600"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Permits Section - Placeholder LEFT, Content RIGHT */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="aspect-[4/3] rounded-xl overflow-hidden relative">
                  <img
                    src={mountainLakesPatioFirePit}
                    alt="Luxury patio pavers with fire pit and pool in Mountain Lakes, Morris County, NJ by Masonry Pro Construction"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width="800"
                    height="600"
                  />
                </div>
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    North Jersey Fire Pit Permits and <span className="text-gradient-gold">Setback Rules</span> You Need to Know
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      Permanent masonry fire pits in New Jersey require a permit under the <CitationLink num={4} url="https://www.nj.gov/dep/parksandforests/fire/permits/recreational.html">Uniform Construction Code</CitationLink>. Gas-fueled fire pits fall under the same requirement, along with any local zoning rules your municipality enforces. It is not something you want to skip, because a failed inspection can mean tearing out finished work.
                    </p>
                    <p>
                      For recreational fires, the state sets a 3-foot diameter and 2-foot height limit. Approved fire pit containers need at least <CitationLink num={1} url="https://www.nfpa.org/news-blogs-and-articles/blogs/2025/09/10/wildfire-prevention-tips-for-fire-pits-and-outdoor-fires">15 feet of clearance from structures and property lines</CitationLink>. If your setup does not meet those standards, the setback jumps to 25 feet.
                    </p>
                    <p>
                      Keep in mind that North Jersey municipalities can add their own restrictions on top of the state code. Homeowners in Wayne and Wyckoff should check with their local building department before breaking ground. What's allowed in one town may not fly in the next one over.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Gas vs Wood Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Gas and Wood-Burning Fire Pits <span className="text-gradient-gold">Each Serve a Different Purpose</span>
                </h2>
                <div className="space-y-4 text-lg text-muted-foreground">
                  <p>
                    We build two types of fire pits in North Jersey: gas and wood-burning. The type you choose depends on how you want to use your backyard. Gas runs on natural gas or propane. You press a button, the flame is on, and there is nothing to clean up after. No smoke, no ash, no chopping wood, and no hauling firewood.
                  </p>
                  <p>
                    People get nostalgic about wood-burning. It's a different atmosphere entirely. But wood smoke can carry <CitationLink num={3} url="https://www.epa.gov/burnwise/backyard-recreational-fires">fine particle pollution, according to the EPA</CitationLink>, and that can matter when your neighbor's deck is 30 feet away. We hear about it frequently from homeowners in Essex County and the denser parts of Passaic County, where the lots are closer together.
                  </p>
                  <p>
                    A gas fire pit requires a licensed professional to connect the gas supply lines, a separate trade from the masonry work itself. Our crew handles the coordination between both, so the project can stay on schedule, and you are not chasing down a second contractor on your own.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Base Prep Section - Content LEFT, Placeholder RIGHT */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Proper Base Prep Prevents Cracking in <span className="text-gradient-gold">North Jersey's Freeze-Thaw Cycles</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      What sits underneath a fire pit matters just as much as the stone or brick on top. We begin by excavating several inches below grade and filling the area with compacted crushed stone. That layer gives water somewhere to go instead of sitting under the structure and freezing.
                    </p>
                    <p>
                      To create a flat, stable surface for the fire pit walls, we add leveling sand on top of the crushed stone. Without it, even a small slope can cause uneven settling over time.
                    </p>
                    <p>
                      North Jersey's clay-rich soils make base preparation more involved than in sandier regions. The ground here holds moisture, and when that moisture freezes and thaws through winter, it can push a fire pit out of level or crack the mortar joints. We encounter this frequently on properties in the Ramapo Valley and along the Passaic River basin, where water tables tend to be higher. Additional drainage planning may be required for those jobs.
                    </p>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden relative">
                  <img 
                    src={kinnelonFirePit} 
                    alt="Fire pit installation with stone surround in Kinnelon, Morris County, NJ by Masonry Pro Construction" 
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width="800"
                    height="600"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Maintenance Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Simple Seasonal Care Keeps a Masonry Fire Pit <span className="text-gradient-gold">in Top Shape</span>
                </h2>
                <div className="space-y-4 text-lg text-muted-foreground">
                  <p>
                    A fire pit that gets regular attention between seasons will last significantly longer than one that does not. Most of the work takes less than an hour and requires no special tools.
                  </p>
                  <p>
                    After each use, let the ashes in a wood-burning fire pit cool completely before removing them. It sounds basic, but leftover ash holds moisture, and moisture trapped against masonry can cause surface damage over time. In winter, a weather-resistant cover keeps snow and ice from sitting directly on the stone and mortar.
                  </p>
                  <p>
                    Each spring, we recommend inspecting the mortar joints for any cracks that may have developed over winter. North Jersey's freeze-thaw cycles are hard on exposed masonry, and small cracks can turn into loose joints if left alone. Repointing early is a straightforward fix compared to rebuilding a section later. For gas fire pits, check the connections and burners before your first use of the season.
                  </p>
                  <p>
                    The <CitationLink num={2} url="https://www.usfa.fema.gov/prevention/outdoor-fires/">U.S. Fire Administration</CitationLink> also recommends keeping fire extinguishing tools nearby during every use. Homeowners from Maplewood to Mahwah can benefit from scheduling a quick visual inspection each spring before lighting up for the season.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Featured Snippet / Checklist Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  What Do You Need for Fire Pit Installation in <span className="text-gradient-gold">North Jersey</span>?
                </h2>
                <ol className="space-y-4 text-lg text-muted-foreground list-decimal pl-6">
                  <li>Check your local municipal fire codes and NJ Uniform Construction Code permit requirements before any work begins.</li>
                  <li>Choose a location at least 15 to 25 feet from structures, fences, and property lines.</li>
                  <li>Select fire-rated masonry materials such as natural stone, concrete block, or brick, along with a stainless steel liner.</li>
                  <li>Excavate the site and install a compacted gravel base, at minimum 6 to 8 inches deep, for proper drainage.</li>
                  <li>Build the fire pit walls using heat-resistant mortar with ventilation openings built into the design.</li>
                  <li>For gas fire pits, have a licensed professional connect the supply lines before the structure is finished.</li>
                  <li>Schedule a final inspection with your local building department before the first use.</li>
                </ol>
              </div>
            </div>
          </section>

          {/* FAQ Section - Card Grid */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
                Frequently Asked Questions
              </h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {faqs.map((faq, index) => (
                  <Card key={index} className="bg-card border-border">
                    <CardHeader>
                      <CardTitle className="text-lg">{faq.question}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          <YouTubeShortsSection 
            title="See Our Work in"
            highlightedTitle="Action"
            videoIds={["KjTHv1gQP9c", "plh-ZGZz65A", "Fogsfyz7P18"]}
          />

          <RelatedServices currentCategory="masonry" />

          {/* CTA Section with Form */}
          <CTASection 
            heading="Ready for a" 
            highlightedHeading="Fire Pit Installation?"
            serviceName="Fire Pit"
            showFinancing
          />

          {/* Citations Accordion */}
          <section className="py-12 bg-muted/30">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="citations" className="border-border">
                    <AccordionTrigger className="text-lg font-semibold hover:no-underline">
                      Citations and References
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-base">
                      <ul className="space-y-3">
                        {citations.map((citation) => (
                          <li key={citation.num} className="text-sm">
                            <sup>{citation.num}</sup>{" "}
                            <a 
                              href={citation.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              {citation.text}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
